/**
 * Arreglo 1.9 (oct-2026) — Importar clientes desde CSV (otra plataforma): vista previa, duplicados por
 * teléfono/correo nunca se repiten, resultado importados / duplicados / errores.
 */
import { useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { clientsApi } from '@/lib/clientsApi';
import { tr } from '@/contexts/LanguageContext';

export const ClientImportSheet = ({ open, onOpenChange, onImported }) => {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [decisions, setDecisions] = useState({}); // 21-oct-2026: línea → new|merge|skip

  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try { setPreview(await clientsApi.importPreview(file)); setDecisions({}); }
    catch (e) { toast.error(e?.payload?.detail || e.message || tr("No pudimos leer el archivo")); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    const rows = (preview?.rows || []).filter((r) => r.status === 'ok').map(({ line, name, last_name, phone, email, notes, name_dup }) => ({
      name, last_name, phone, email, notes, ...(name_dup ? { decision: decisions[line] || 'new', name_dup_id: name_dup.id } : {}) }));
    if (!rows.length) { toast.error(tr("No hay filas válidas para importar")); return; }
    setBusy(true);
    try { const r = await clientsApi.importConfirm(rows); setResult(r); onImported?.(); toast.success(tr("{0} clientes importados", r.imported)); }
    catch (e) { toast.error(e?.payload?.detail || e.message || tr("Error al importar")); }
    finally { setBusy(false); }
  };

  const reset = () => { setPreview(null); setResult(null); };
  const sum = preview?.summary;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <SheetContent side="bottom" aria-describedby={undefined} className="bg-card border-border rounded-t-2xl max-h-[92vh] overflow-y-auto " data-testid="client-import-sheet">
        <SheetHeader className="text-left"><SheetTitle className="font-display text-lg">{tr("Importar clientes (CSV)")}</SheetTitle></SheetHeader>
        <div className="mt-3 space-y-3">
          {!preview && !result && (
            <div className="space-y-2" data-testid="client-import-step-pick">
              <p className="text-xs text-muted-foreground">{tr("Exporta tus clientes desde tu otra plataforma como CSV con columnas")}{' '}<b>{tr("nombre, apellido, teléfono, email, notas")}</b>{' '}{tr("(en cualquier orden). Los teléfonos ya existentes no se duplican.")}</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid="client-import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="client-import-pick-btn"><Upload className="w-4 h-4 mr-2" /> {busy ? tr("Leyendo…") : tr("Elegir archivo CSV")}</Button>
            </div>
          )}
          {preview && !result && (
            <div className="space-y-2" data-testid="client-import-step-preview">
              <div className="grid grid-cols-3 gap-1.5 text-center">
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-2"><p className="font-display font-bold text-lg" data-testid="client-import-ok">{sum.ok}</p><p className="text-[10px] text-muted-foreground">{tr("Nuevos")}</p></div>
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-2"><p className="font-display font-bold text-lg" data-testid="client-import-dup">{sum.duplicates}</p><p className="text-[10px] text-muted-foreground">{tr("Duplicados")}</p></div>
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-2"><p className="font-display font-bold text-lg" data-testid="client-import-err">{sum.errors}</p><p className="text-[10px] text-muted-foreground">{tr("Con error")}</p></div>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1"><FileSpreadsheet className="w-3.5 h-3.5" />{' '}{tr("Columnas detectadas: {0}", Object.entries(preview.columns || {}).map(([k, v]) => `${v} → ${k}`).join(', '))}</p>
              <div className="max-h-56 overflow-y-auto rounded-xl border border-border text-xs" data-testid="client-import-rows">
                <table className="w-full"><tbody>
                  {(preview.rows || []).slice(0, 300).map((r) => (
                    <tr key={r.line} className={`border-b border-border/60 ${r.status === 'ok' ? '' : r.status === 'duplicate' ? 'text-amber-300' : 'text-destructive'}`} data-testid={`client-import-row-${r.line}`}>
                      <td className="px-2 py-1 text-muted-foreground w-8">{r.line}</td>
                      <td className="px-2 py-1 truncate max-w-[120px]">{[r.name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-2 py-1">{r.phone || r.email || '—'}</td>
                      <td className="px-2 py-1 text-right">
                        {r.status === 'ok' && r.name_dup ? (
                          <select value={decisions[r.line] || 'new'} onChange={(e) => setDecisions({ ...decisions, [r.line]: e.target.value })} className="h-7 rounded-lg border border-amber-500/50 bg-card px-1 text-[11px] text-foreground" title={`≈ ${r.name_dup.name}`} data-testid={`client-import-name-dup-${r.line}`}>
                            <option value="new">{tr("¿{0}? Otra persona", r.name_dup.name)}</option>
                            <option value="merge">{tr("Misma persona (sumar contacto)")}</option>
                            <option value="skip">{tr("Omitir")}</option>
                          </select>
                        ) : (r.status === 'ok' ? tr("nuevo") : r.status === 'duplicate' ? 'duplicado' : r.reason)}
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} className="flex-1 h-11 rounded-full" data-testid="client-import-back">{tr("Otro archivo")}</Button>
                <Button onClick={confirm} disabled={busy || !sum.ok} className="flex-1 h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="client-import-confirm">{busy ? tr("Importando…") : tr("Importar {0}", sum.ok)}</Button>
              </div>
            </div>
          )}
          {result && (
            <div className="text-center py-6 space-y-2" data-testid="client-import-result">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <p className="font-display text-xl font-bold">{tr("{0} importados", result.imported)}</p>
              <p className="text-xs text-muted-foreground">{tr("{0} duplicados omitidos · {1} con error", result.duplicates, result.errors)}</p>
              <Button onClick={() => onOpenChange(false)} className="h-10 rounded-full bg-primary text-primary-foreground px-8" data-testid="client-import-done">{tr("Listo")}</Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ClientImportSheet;
